import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import styles from './RichTextEditor.module.css';
import { isEquivalentRichTextHtml, normalizeRichTextHtml } from '../utils/sanitizeRichTextHtml';
import { findMentionRanges } from '../utils/mentionHighlight';
import type { MentionKind, MentionTerm } from '../utils/mentionHighlight';
import { EmployeeAvatar } from './EmployeeAvatar';
import { AiAgentAvatar } from './AiAgentAvatar';

export type MentionOption = {
  id: string;
  name: string;
  subtitle?: string;
  kind?: MentionKind;
  avatarUrl?: string | null;
  colorPreference?: string;
};

export type { MentionTerm } from '../utils/mentionHighlight';

const mentionDecorationKey = new PluginKey('mentionChipDecorations');

/**
 * Highlights known `@Name` mentions in the editor with chip styling. Pure view decorations:
 * nothing is written into the document, so the saved HTML stays plain text.
 */
const createMentionDecorationExtension = (getTerms: () => readonly MentionTerm[]) =>
  Extension.create({
    name: 'mentionChipDecorations',
    addProseMirrorPlugins() {
      const build = (doc: ProseMirrorNode) => {
        const terms = getTerms();
        if (terms.length === 0) {
          return DecorationSet.empty;
        }

        const decorations: Decoration[] = [];
        doc.descendants((node, pos) => {
          if (!node.isText || !node.text) {
            return;
          }

          for (const range of findMentionRanges(node.text, terms)) {
            decorations.push(Decoration.inline(pos + range.start, pos + range.end, {
              class: range.kind === 'ai' ? `${styles.mentionChip} ${styles.mentionChipAi}` : styles.mentionChip,
              'data-mention-kind': range.kind,
            }));
          }
        });

        return DecorationSet.create(doc, decorations);
      };

      return [
        new Plugin({
          key: mentionDecorationKey,
          state: {
            init: (_, state) => build(state.doc),
            apply: (tr, previous) => (tr.docChanged || tr.getMeta(mentionDecorationKey) ? build(tr.doc) : previous),
          },
          props: {
            decorations(state) {
              return mentionDecorationKey.getState(state) as DecorationSet | undefined;
            },
          },
        }),
      ];
    },
  });

const highlightQuery = (name: string, query: string, className: string): React.ReactNode => {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) {
    return name;
  }

  const index = name.toLowerCase().indexOf(normalizedQuery.toLowerCase());
  if (index < 0) {
    return name;
  }

  return (
    <>
      {name.slice(0, index)}
      <mark className={className}>{name.slice(index, index + normalizedQuery.length)}</mark>
      {name.slice(index + normalizedQuery.length)}
    </>
  );
};

type MentionTrigger = {
  start: number;
  end: number;
  query: string;
};

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  ariaLabel?: string;
  mentionSuggestionsProvider?: (query: string) => Promise<MentionOption[]>;
  mentionSuggestionsLabel?: string;
  /** Short keyboard hint shown in the suggestion menu header. */
  mentionSuggestionsHint?: string;
  /** Known mentionable names; matching `@Name` text is rendered as a chip while editing. */
  mentionHighlightTerms?: readonly MentionTerm[];
  /** Inserts HTML at the caret (or last selection) each time a request with a new `id` arrives. */
  insertRequest?: { id: number; html: string } | null;
};

const findMentionTrigger = (text: string): { query: string; mentionLength: number } | null => {
  const atIndex = text.lastIndexOf('@');
  if (atIndex < 0) {
    return null;
  }

  if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) {
    return null;
  }

  const rawQuery = text.slice(atIndex + 1);
  if (/\s/.test(rawQuery)) {
    return null;
  }

  return {
    query: rawQuery,
    mentionLength: rawQuery.length + 1,
  };
};

export const RichTextEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  minHeight = 80,
  ariaLabel,
  mentionSuggestionsProvider,
  mentionSuggestionsLabel,
  mentionSuggestionsHint,
  mentionHighlightTerms,
  insertRequest,
}) => {
  const [mentionTrigger, setMentionTrigger] = useState<MentionTrigger | null>(null);
  const [mentionSuggestions, setMentionSuggestions] = useState<MentionOption[]>([]);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const mentionRequestIdRef = useRef(0);
  const editorAreaRef = useRef<HTMLDivElement>(null);
  const [mentionMenuStyle, setMentionMenuStyle] = useState<React.CSSProperties>();
  const mentionTermsRef = useRef<readonly MentionTerm[]>(mentionHighlightTerms ?? []);
  mentionTermsRef.current = mentionHighlightTerms ?? [];

  const mentionsEnabled = useMemo(() => Boolean(mentionSuggestionsProvider), [mentionSuggestionsProvider]);

  // Built once; reads the latest terms through the ref so the editor is not recreated per change.
  const mentionDecorationExtension = useMemo(
    () => createMentionDecorationExtension(() => mentionTermsRef.current),
    [],
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      mentionDecorationExtension,
    ],
    content: value,
    onUpdate({ editor: e }) {
      const html = e.isEmpty ? '' : e.getHTML();
      onChange(html);

      if (!mentionsEnabled) {
        return;
      }

      const selection = e.state.selection;
      const textBeforeCaret = e.state.doc.textBetween(0, selection.from, '\n', '\0');
      const trigger = findMentionTrigger(textBeforeCaret);
      if (!trigger) {
        setMentionTrigger(null);
        setMentionSuggestions([]);
        setMentionMenuOpen(false);
        setMentionActiveIndex(0);
        return;
      }

      const start = selection.from - trigger.mentionLength;
      if (start < 0) {
        setMentionTrigger(null);
        setMentionSuggestions([]);
        setMentionMenuOpen(false);
        setMentionActiveIndex(0);
        return;
      }

      setMentionTrigger({
        start,
        end: selection.from,
        query: trigger.query,
      });
    },
  });

  const closeMentionMenu = useCallback(() => {
    setMentionMenuOpen(false);
    setMentionActiveIndex(0);
    setMentionSuggestions([]);
    setMentionTrigger(null);
  }, []);

  const onSelectMention = useCallback((option: MentionOption) => {
    if (!editor || !mentionTrigger) {
      return;
    }

    editor
      .chain()
      .focus()
      .deleteRange({ from: mentionTrigger.start, to: mentionTrigger.end })
      .insertContent(`@${option.name} `)
      .run();

    closeMentionMenu();
  }, [closeMentionMenu, editor, mentionTrigger]);

  const onEditorKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!mentionMenuOpen || mentionSuggestions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionActiveIndex(current => (current + 1) % mentionSuggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionActiveIndex(current => (current - 1 + mentionSuggestions.length) % mentionSuggestions.length);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      onSelectMention(mentionSuggestions[mentionActiveIndex]);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMentionMenu();
    }
  }, [closeMentionMenu, mentionActiveIndex, mentionMenuOpen, mentionSuggestions, onSelectMention]);

  const updateMentionMenuPosition = useCallback(() => {
    const area = editorAreaRef.current;
    if (!area) {
      return;
    }

    const rect = area.getBoundingClientRect();
    const gap = 6;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceAbove >= 160 || spaceAbove >= spaceBelow;
    const available = Math.max(120, (openAbove ? spaceAbove : spaceBelow) - gap - 8);

    setMentionMenuStyle({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(320, available),
      ...(openAbove
        ? { top: 'auto', bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap, bottom: 'auto' }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!mentionMenuOpen) {
      return;
    }

    updateMentionMenuPosition();
    window.addEventListener('resize', updateMentionMenuPosition);
    window.addEventListener('scroll', updateMentionMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMentionMenuPosition);
      window.removeEventListener('scroll', updateMentionMenuPosition, true);
    };
  }, [mentionMenuOpen, updateMentionMenuPosition]);

  useEffect(() => {
    if (!mentionsEnabled || !mentionSuggestionsProvider) {
      return;
    }

    if (!mentionTrigger) {
      mentionRequestIdRef.current += 1;
      setMentionSuggestions([]);
      setMentionMenuOpen(false);
      setMentionActiveIndex(0);
      return;
    }

    const requestId = mentionRequestIdRef.current + 1;
    mentionRequestIdRef.current = requestId;

    const timeoutId = window.setTimeout(async () => {
      try {
        const options = await mentionSuggestionsProvider(mentionTrigger.query);
        if (requestId !== mentionRequestIdRef.current) {
          return;
        }

        setMentionSuggestions(options);
        setMentionMenuOpen(options.length > 0);
        setMentionActiveIndex(0);
      } catch {
        if (requestId !== mentionRequestIdRef.current) {
          return;
        }

        setMentionSuggestions([]);
        setMentionMenuOpen(false);
      }
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [mentionSuggestionsProvider, mentionTrigger, mentionsEnabled]);

  // Known names arrive async (employee list); nudge the decoration plugin so existing text re-renders.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta(mentionDecorationKey, true));
  }, [editor, mentionHighlightTerms]);

  const handledInsertIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!editor || editor.isDestroyed || !insertRequest || handledInsertIdRef.current === insertRequest.id) {
      return;
    }

    handledInsertIdRef.current = insertRequest.id;
    editor.chain().focus().insertContent(insertRequest.html).run();
  }, [editor, insertRequest]);

  // Sync external value changes (e.g. form reset / task load), but avoid clobbering active edits.
  useEffect(() => {
    if (!editor) return;
    if (editor.isFocused) return;

    const current = editor.isEmpty ? '' : editor.getHTML();
    if (isEquivalentRichTextHtml(current, value)) {
      return;
    }

    editor.commands.setContent(normalizeRichTextHtml(value), { emitUpdate: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const btn = (label: string, active: boolean, onClick: () => void, title: string) => (
    <button
      key={title}
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`${styles.toolbarBtn}${active ? ` ${styles.active}` : ''}`}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.wrapper} aria-label={ariaLabel} role="group">
      <div className={styles.toolbar} role="toolbar" aria-label="Text formatting">
        {btn('B', editor.isActive('bold'),        () => editor.chain().focus().toggleBold().run(),          'Bold')}
        {btn('I', editor.isActive('italic'),      () => editor.chain().focus().toggleItalic().run(),        'Italic')}
        {btn('U', editor.isActive('underline'),   () => editor.chain().focus().toggleUnderline().run(),     'Underline')}
        {btn('S̶', editor.isActive('strike'),      () => editor.chain().focus().toggleStrike().run(),        'Strikethrough')}
        <span className={styles.toolbarDivider} />
        {btn('H1', editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'Heading 1')}
        {btn('H2', editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'Heading 2')}
        <span className={styles.toolbarDivider} />
        {btn('≡', editor.isActive('bulletList'),  () => editor.chain().focus().toggleBulletList().run(),    'Bullet list')}
        {btn('①', editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(),   'Numbered list')}
        <span className={styles.toolbarDivider} />
        {btn('"', editor.isActive('blockquote'),  () => editor.chain().focus().toggleBlockquote().run(),    'Blockquote')}
        {btn('<>', editor.isActive('code'),        () => editor.chain().focus().toggleCode().run(),          'Inline code')}
        <span className={styles.toolbarDivider} />
        {btn('✕', false, () => editor.chain().focus().clearNodes().unsetAllMarks().run(), 'Clear formatting')}
      </div>
      <div
        ref={editorAreaRef}
        className={styles.editorArea}
        style={{ minHeight }}
        onClick={() => editor.commands.focus()}
      >
        <EditorContent editor={editor} onKeyDown={onEditorKeyDown} />
      </div>
      {mentionMenuOpen && mentionSuggestions.length > 0
        ? createPortal(
          <div
            className={`${styles.mentionMenu} ${styles.mentionMenuFloating}`}
            style={mentionMenuStyle}
            role="listbox"
            aria-label={mentionSuggestionsLabel || 'Mention suggestions'}
          >
            {(mentionSuggestionsLabel || mentionSuggestionsHint) ? (
              <div className={styles.mentionMenuHeader}>
                {mentionSuggestionsLabel ? <span className={styles.mentionMenuTitle}>{mentionSuggestionsLabel}</span> : null}
                {mentionSuggestionsHint ? <span className={styles.mentionMenuHint}>{mentionSuggestionsHint}</span> : null}
              </div>
            ) : null}
            <div className={styles.mentionMenuList}>
              {mentionSuggestions.map((option, index) => {
                const isActive = index === mentionActiveIndex;
                const isAi = option.kind === 'ai';
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={[
                      styles.mentionMenuItem,
                      isActive ? styles.mentionMenuItemActive : '',
                      isAi ? styles.mentionMenuItemAi : '',
                    ].filter(Boolean).join(' ')}
                    onMouseEnter={() => setMentionActiveIndex(index)}
                    onMouseDown={event => {
                      event.preventDefault();
                      onSelectMention(option);
                    }}
                  >
                    {isAi ? (
                      <AiAgentAvatar name={option.name} size={28} className={styles.mentionMenuAvatar} />
                    ) : (
                      <EmployeeAvatar
                        name={option.name}
                        imageUrl={option.avatarUrl ?? undefined}
                        colorPreference={option.colorPreference}
                        size={28}
                        className={styles.mentionMenuAvatar}
                      />
                    )}
                    <span className={styles.mentionMenuText}>
                      <span className={styles.mentionMenuNameRow}>
                        <span className={styles.mentionMenuName}>
                          {highlightQuery(option.name, mentionTrigger?.query ?? '', styles.mentionMenuMatch)}
                        </span>
                        {isAi ? <span className={styles.mentionMenuAiBadge}>AI</span> : null}
                      </span>
                      {option.subtitle ? <span className={styles.mentionMenuMeta}>{option.subtitle}</span> : null}
                    </span>
                    {isActive ? <span className={styles.mentionMenuEnterHint} aria-hidden="true">↵</span> : null}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
};
